import { Component, Input, OnInit, AfterViewInit } from '@angular/core';
import { AppState } from '../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { DetailState, selectDetailState, selectvolumeDetailState } from '../../detail-state';
import { filter, distinctUntilChanged, take } from 'rxjs/operators';
import { StoreVolume } from '../../../core/store/volume/store-volume';
import { formatAsCurrency } from '../../../core/util/string-utils';
import { DefinitionService } from '../../../core/definition/definition.service';
import { Definition } from '../../../core/definition/definition';
import { StoreStatusType, ActiveStoreStatuses, FutureStoreStatuses, HistoricalStoreStatuses } from '../../../core/identity/constant/store-status-type.enum';
import * as _ from 'lodash';
import { StoreHighlight } from '../store-critical-insights-panel/store-highlights-panel/store-highlight';
import { HighlightsDataService } from '../../../core/services/highlights-data.service';
import { SalesPerformanceDataService } from '../../../core/services/sales-performance-data.service';
import { AdditionalInsightsDataService } from '../../../core/services/additional-insights-data.service';
import { AuthorizationAwareComponent } from '../../../core/authorization-aware-component';
import { DefinitionType } from '../../../core/definition/definition-type.enum';
import { selectUserState, UserState } from '../../../auth/user-state';
import { SalesVolumeDisplayType } from '../../../core/user-preferences/sales-volume-display-type.enum';
import { UserPreferences } from '../../../core/user-preferences/user-preferences';

@Component({
  standalone: false,
  selector: 'mtn-store-detail-pdf-first-page',
  templateUrl: './store-detail-pdf-first-page.component.html',
  styleUrls: ['./store-detail-pdf-first-page.component.scss']
})
export class StoreDetailPdfFirstPageComponent extends AuthorizationAwareComponent implements OnInit, AfterViewInit {
  @Input() store: any;
  @Input() additionalInsights: any;
  volume: StoreVolume;
  cardTitleStatus: string;
  highlights: StoreHighlight[] = [];
  DefinitionType = DefinitionType;
  salesVolumeDisplayMode: SalesVolumeDisplayType = SalesVolumeDisplayType.WEEKLY;
  preferences: UserPreferences;

  // Track loaded status to prevent repeated API calls
  private loadedStatusForStoreUuid: string = null;

  constructor(protected ngrxStore: NgrxStore<AppState>,
              private definitionService: DefinitionService,
              private highlightsDataService: HighlightsDataService,
              private salesPerformanceDataService: SalesPerformanceDataService,
              private additionalInsightsDataService: AdditionalInsightsDataService) {
    super(ngrxStore);
  }

  onAuthorizationChange(): void {}
  onAuthorizationInit(): void {}

  ngOnInit(): void {
    super.ngOnInit();
    this.subscribeToUserPreferences();
    this.subscribeToDetailState();
    this.subscribeToVolumeState();
    this.subscribeToHighlights();
    this.subscribeToSalesPerformance();
    this.subscribeToAdditionalInsights();
  }

  ngAfterViewInit(): void {
    // Render charts after view initialization
    setTimeout(() => this.renderMiniCharts(), 100);
  }

  private renderMiniCharts(): void {
    const canvases = document.querySelectorAll('.mini-chart');
    canvases.forEach((canvas: HTMLCanvasElement) => {
      const percentile = parseInt(canvas.getAttribute('data-percentile') || '0');
      const color = canvas.getAttribute('data-color') || '#ddd';
      this.drawMiniChart(canvas, percentile, color);
    });
  }

  private drawMiniChart(canvas: HTMLCanvasElement, percentile: number, color: string): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 15;
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + (2 * Math.PI * Math.max(percentile, 1) / 100);

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw progress arc - ensure minimum visibility for 0 percentile
    if (percentile > 0 || color) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.strokeStyle = color || '#ff0000'; // Default to red if no color
      ctx.lineWidth = 3;
      ctx.stroke();
    }
  }

  private subscribeToUserPreferences(): void {
    this.addSubscription(
      selectUserState(this.ngrxStore)
        .pipe(filter((state: UserState) => !!state.userPreferences))
        .subscribe((state: UserState) => {
          this.preferences = state.userPreferences;
          this.salesVolumeDisplayMode = state.userPreferences.salesVolumeDisplayMode || SalesVolumeDisplayType.WEEKLY;
          this.updateCriticalInsights();
        })
    );
  }

  private subscribeToDetailState(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .pipe(
          filter((state: DetailState) => !!state.store),
          distinctUntilChanged((prev, curr) => prev.store?.uuid === curr.store?.uuid)
        )
        .subscribe((state: DetailState) => {
          this.store = state.store;
          this.updateStoreInfo();
          this.updateCriticalInsights();
        })
    );
  }

  private subscribeToVolumeState(): void {
    this.addSubscription(
      selectvolumeDetailState(this.ngrxStore)
        .pipe(distinctUntilChanged((prev, curr) => prev === curr))
        .subscribe((volume) => {
          this.volume = volume;
          this.updateCriticalInsights();
        })
    );
  }

  private updateStoreInfo(): void {
    if (this.store) {
      this.storeInfo = {
        name: `${this.store.name || this.store.banner?.name || 'STORE'} ${this.store.number ? '(' + this.store.number + ')' : ''}`,
        type: `${this.store.banner?.name || 'Store'} - ${this.store.fit || 'Store Type'}`,
        address: this.store.space?.location?.addressLine1 || 'Address Not Available',
        city: this.getFormattedAddress(),
        location: this.store.space?.shoppingCenter?.name || 'Unnamed Shopping Center',
        locationType: this.getShoppingCenterDetails(),
        salesVolume: this.store.estimatedWeeklySales?.toLocaleString() || '0',
        salesPerSqft: `${(this.store.estimatedWeeklySalesPerSqft || 0).toFixed(2)} sqft`,
        salesArea: `${this.store.space?.salesArea?.toLocaleString() || '0'}/${this.store.space?.totalArea?.toLocaleString() || '0'} sqft`,
        status: this.store.isOpen ? 'Open' : 'Closed'
      };
    }
  }

  private getFormattedAddress(): string {
    if (!this.store?.space?.location) return '';
    const location = this.store.space.location;
    const parts = [];
    if (location.city) parts.push(location.city + ',');
    if (location.state) parts.push(location.state);
    if (location.postalCode) parts.push(location.postalCode);
    return parts.join(' ');
  }

  private getShoppingCenterDetails(): string {
    const shoppingCenter = this.store?.space?.shoppingCenter;
    if (!shoppingCenter) return 'Unknown Owner - Unknown Shopping Center Type';

    const owner = shoppingCenter.ownerCompany?.name || 'Unknown Owner';
    const type = shoppingCenter.type || 'Unknown Shopping Center Type';
    return `${owner} - ${type}`;
  }

  private updateCriticalInsights(): void {
    const displaySuffix = this.salesVolumeDisplayMode === SalesVolumeDisplayType.ANNUAL ? 'Annually' : 'Weekly';

    // Sales Volume from volume data
    if (this.volume?.total) {
      this.metricsData[0] = {
        value: formatAsCurrency(this.volume.total, false),
        label: `Est. Sales Volume (${displaySuffix}) ${this.volume.date ? new Date(this.volume.date).toLocaleDateString('en-US', {month: '2-digit', year: 'numeric'}) : ''}`
      };
      console.log("hello - ",this.volume);
    } else {
      this.metricsData[0] = {
        value: this.volume ? 'Not Purchased' : 'Not Available',
        label: `Sales Volume (${displaySuffix})`
      };
    }

    // Sales per SQFT from volume data
    if (this.volume?.salesSqft) {
      this.metricsData[1] = {
        value: `${formatAsCurrency(this.volume.salesSqft)} /sqft`,
        label: `Est. Sales/SQFT (${displaySuffix}, Total Area)`
      };
    } else {
      this.metricsData[1] = {
        value: this.volume ? 'Not Purchased' : 'Not Available',
        label: `Sales/SQFT (${displaySuffix}, Total Area)`
      };
    }

    // Sales Area from store data
    if (this.store) {
      this.metricsData[2] = {
        value: `${this.store.grocerySalesArea?.toLocaleString() || 'Unknown'} / ${this.store.totalArea?.toLocaleString() || 'Unknown'} sqft`,
        label: 'Sales / Total Area'
      };

      // Store Status - using definition service like the original panel
      // Only call API if status hasn't been loaded for this store yet
      const status = this.store.getCurrentStatus();
      if (status && this.loadedStatusForStoreUuid !== this.store.uuid) {
        this.loadedStatusForStoreUuid = this.store.uuid;
        this.definitionService.findOneStoreStatusType(status.getStatusSystemName())
          .pipe(take(1))
          .subscribe((definition: Definition<StoreStatusType>) => {
            this.cardTitleStatus = definition?.displayName || 'Unknown';
            this.metricsData[3] = {
              value: this.cardTitleStatus,
              label: 'Store Status'
            };
          });
      } else if (!status) {
        this.cardTitleStatus = 'Unknown';
        this.metricsData[3] = {
          value: this.cardTitleStatus,
          label: 'Store Status'
        };
      }
    }
  }

  getMetricIcon(index: number): string {
    const icons = [
      'assets/images/FreeSample-Vectorizer-io-389687af1c7ca0c4fa762732ed5d95852f9e0f5e.svg',
      'assets/images/FreeSample-Vectorizer-io-98d91d54b0948e6a426925ccfd1d76ab3b13a652.svg',
      'assets/images/FreeSample-Vectorizer-io-e8f4b7b33da0101f2fbc50f99383950056b6d378.svg'
    ];
    return icons[index];
  }

  getStatusColor(): string {
    if (!this.store) return 'limegreen';

    const status = this.store.getCurrentStatus();
    if (!status) return 'limegreen';

    const statusName = status.getStatusSystemName();

    if (_.includes(FutureStoreStatuses, statusName)) {
      return 'dodgerblue';
    } else if (_.includes(ActiveStoreStatuses, statusName)) {
      return 'limegreen';
    } else if (_.includes(HistoricalStoreStatuses, statusName)) {
      return 'red';
    }

    return 'limegreen'; // default green
  }

  private subscribeToHighlights(): void {
    this.addSubscription(
      this.highlightsDataService.highlights$.subscribe((highlights: StoreHighlight[]) => {
        this.highlights = highlights;
      })
    );
  }

  shouldShowSalesPerformanceInFirstPage(): boolean {
    return this.highlights.length <= 4;
  }

  private subscribeToSalesPerformance(): void {
    this.addSubscription(
      this.salesPerformanceDataService.salesPerformance$.subscribe((data: any) => {
        // Sort data to match UI order: DRIVE_TIME, MARKET_AREA+ALL, MARKET_AREA+BANNER
        this.salesPerformance = {
          byVolume: this.sortComparisonData(data.byVolume),
          bySqft: this.sortComparisonData(data.bySqft)
        };
        // Re-render charts when data updates
        setTimeout(() => this.renderMiniCharts(), 50);
      })
    );
  }

  private sortComparisonData(items: any[]): any[] {
    if (!items || items.length === 0) return [];

    return items.sort((a, b) => {
      const aComp = a.chartData?.comparison;
      const bComp = b.chartData?.comparison;

      if (!aComp || !bComp) return 0;

      // Order: DRIVE_TIME, MARKET_AREA+ALL, MARKET_AREA+BANNER
      const getOrder = (comp: any) => {
        if (comp.region === 'DRIVE_TIME') return 1;
        if (comp.region === 'MARKET_AREA' && comp.comparisonType === 'ALL') return 2;
        if (comp.region === 'MARKET_AREA' && comp.comparisonType === 'BANNER') return 3;
        return 4;
      };

      return getOrder(aComp) - getOrder(bComp);
    });
  }


  storeInfo = {
    name: 'WALMART (2288)',
    type: 'Walmart - Supercenter',
    address: '80 Rio Rancho Rd',
    city: 'Pomona, CA 91766',
    location: 'Unnamed Shopping Center',
    locationType: 'Castleton - Community Center',
    salesVolume: '540,000',
    salesPerSqft: '4.17 sqft',
    salesArea: '40,000/1,29,645 sqft',
    status: 'Open'
  };

  metricsData = [
    { value: '540,000', label: 'Est. Sales Volume (Weekly) 04/2025' },
    { value: '4.17 sqft', label: 'Est. Sales/SQFT (Weekly, Total Area)' },
    { value: '40,000/1,29,645 sqft', label: 'Sales/ Total Area' },
    { value: 'Open', label: 'Store Status' }
  ];



  salesPerformance = {
    byVolume: [],
    bySqft: []
  };

  storeRatings: any= [
    [
      { name: 'Ceiling Condition', rating: '3/5' },
      { name: 'Checkstand Condition', rating: '3/5' }
    ],
    [
      { name: 'Exterior Condition', rating: '4/5' },
      { name: 'Floor Condition', rating: '3/5' }
    ],
    [
      { name: 'Refrigeration Condition', rating: '3/5' },
      { name: 'Shelving Condition', rating: '3/5' }
    ],
    [
      { name: 'Visibility from Road', rating: '3/5' },
      { name: 'Wall Condition', rating: '3/5' }
    ]
  ];

  parkingLotRatings: any = [
    [
      { name: 'General Condition', rating: 'NA' },
      { name: 'Ingress/Egress', rating: '5/5' }
    ],
    [
      { name: 'Lighting', rating: '5/5' },
      { name: 'Traffic Flow', rating: '5/5' }
    ]
  ];

  cotenantRatings: any = [
    [
      { name: 'Exterior Condition', rating: '5/5' },
      { name: 'Synergy', rating: '5/5' }
    ]
  ];

  additionalInfo = {
    servicesCount: 0,
    services: [],
    emenities: [],
    analystRatingDate: 'Not Available'
  };

  private subscribeToAdditionalInsights(): void {
    this.addSubscription(
      this.additionalInsightsDataService.additionalInsights$.subscribe((data: any) => {
        this.updateAdditionalInsights(data);
      })
    );
  }

  private updateAdditionalInsights(data: any): void {
    // Update services info
    if (data.services?.surveyDate) {
      this.additionalInfo.servicesCount = data.services.count();
      this.additionalInfo.services = this.getAvailableServices(data.services);
       this.additionalInfo.emenities = this.getAvailableEmenities(data.services);
    }

    // Update ratings info
    if (data.ratings?.hasMinimumRequiredRatingCount()) {
      const date = new Date(data.ratings.ratingDate);
      this.additionalInfo.analystRatingDate = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      this.updateRatingsData(data.ratings);
    }
  }

  private getAvailableServices(services: any) {
    const availableServices = [];
    if (services.bakery) availableServices.push('Bakery');
    if (services.deli) availableServices.push('Deli');
    if (services.meat) availableServices.push('Meat');
    if (services.seafood) availableServices.push('Seafood');
    if (services.pharmacy) availableServices.push('Pharmacy');
    if (services.floral) availableServices.push('Floral');
    if (services.cheese) availableServices.push('Cheese');
    return availableServices;
  }

  private getAvailableEmenities(services: any) {
    const availableEmenities = [];
    if (services.wine) availableEmenities.push('Wine');
    if (services.sushi) availableEmenities.push('Sushi');
    if (services.seating) availableEmenities.push('Seating Area');
    if (services.saladBar) availableEmenities.push('Salad Bar');
    if (services.onlinePickup) availableEmenities.push('Online Pickup');
    if (services.oliveBar) availableEmenities.push('Olive Bar');
    if (services.liquor) availableEmenities.push('Liquor');
    if (services.inStoreRestaurant) availableEmenities.push('In-Store Restaurant');
    if (services.fuel) availableEmenities.push('Fuel');
    if (services.extensivePreparedFoods) availableEmenities.push('Extensive Prepared Foods');
    if (services.expandedGM) availableEmenities.push('Expanded General Merchandise');
    if (services.coffee) availableEmenities.push('Coffee Shop');
    if (services.bulk) availableEmenities.push('Bulk');
    if (services.beer) availableEmenities.push('Beer');
    if (services.bank) availableEmenities.push('Bank');
    return availableEmenities;
  }


  private updateRatingsData(ratings: any): void {
    // Update store ratings using the same method as the wizard
    this.storeRatings = [
      [
        { name: 'Ceiling Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_CEILING_CONDITION')?.value) },
        { name: 'Checkstand Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_CHECKSTAND_CONDITION')?.value) }
      ],
      [
        { name: 'Exterior Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_EXTERIOR_CONDITION')?.value) },
        { name: 'Floor Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_FLOOR_CONDITION')?.value) }
      ],
      [
        { name: 'Refrigeration Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_REFRIGERATION_CONDITION')?.value) },
        { name: 'Shelving Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_SHELVING_CONDITION')?.value) }
      ],
      [
        { name: 'Visibility from Road', rating: this.formatRating(ratings.getRatingOfType('STORE_VISIBILITY')?.value) },
        { name: 'Wall Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_WALL_CONDITION')?.value) }
      ]
    ];

    // Update parking lot ratings (only parking lot data)
    this.parkingLotRatings = [
      [
        { name: 'General Condition', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_CONDITION')?.value) },
        { name: 'Ingress/Egress', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_INGRESS_EGRESS')?.value) }
      ],
      [
        { name: 'Lighting', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_LIGHTING')?.value) },
        { name: 'Traffic Flow', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_FLOW')?.value) }
      ]
    ];

    // Update cotenant ratings (separate array)
    this.cotenantRatings = [
      [
        { name: 'Exterior Condition', rating: this.formatRating(ratings.getRatingOfType('COTENANT_EXTERIOR_CONDITION')?.value) },
        { name: 'Synergy', rating: this.formatRating(ratings.getRatingOfType('COTENANT_SYNERGY')?.value) }
      ]
    ];
  }

  private formatRating(value: number): string {
    return value ? `${value}/5` : 'NA';
  }

  getCriticalInsightsCount(): number {
    return this.highlights.length;
  }
}
